<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <!-- Navigation Header -->
    <header class="fixed top-0 left-0 right-0 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-20">
          <!-- Logo -->
          <div class="flex items-center space-x-3">
            <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy Logo" class="w-12 h-12 rounded-2xl shadow-lg shadow-emerald-500/25" />
            <span class="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              OilBuddy
            </span>
            <span class="px-2 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-xs text-emerald-400 font-medium">
              AI
            </span>
          </div>

          <!-- Navigation Links -->
          <nav class="hidden md:flex items-center space-x-8">
            <a href="#features" class="text-slate-300 hover:text-emerald-400 transition-all duration-300 font-medium">Features</a>
            <a href="#how-it-works" class="text-slate-300 hover:text-emerald-400 transition-all duration-300 font-medium">How it Works</a>
            <a href="#pricing" class="text-slate-300 hover:text-emerald-400 transition-all duration-300 font-medium">Pricing</a>
            <a href="#testimonials" class="text-slate-300 hover:text-emerald-400 transition-all duration-300 font-medium">Testimonials</a>
            <router-link to="/blog" class="text-slate-300 hover:text-emerald-400 transition-all duration-300 font-medium">Blog</router-link>
            <a href="/#/login" class="text-emerald-400 hover:text-emerald-300 font-semibold transition-all duration-300">Sign In</a>
            <a href="/#/admin/login" class="text-slate-300 hover:text-emerald-400 font-semibold transition-all duration-300">Admin</a>
            <router-link to="/register" class="btn-primary-modern">
              Get Started Free
              <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
              </svg>
            </router-link>
          </nav>

          <!-- Mobile Menu Button -->
          <button @click="mobileMenuOpen = !mobileMenuOpen" class="md:hidden p-2 text-slate-300 hover:text-emerald-400 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
          </button>
        </div>

        <!-- Mobile Menu -->
        <div v-if="mobileMenuOpen" class="md:hidden py-4 border-t border-slate-700/50">
          <div class="space-y-2">
            <a href="#features" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Features</a>
            <a href="#how-it-works" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">How it Works</a>
            <a href="#pricing" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Pricing</a>
            <a href="#testimonials" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Testimonials</a>
            <router-link to="/blog" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Blog</router-link>
            <a href="/#/login" class="block px-3 py-3 text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Sign In</a>
            <a href="/#/admin/login" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Admin</a>
            <router-link to="/register" class="block px-3 py-3 btn-primary-modern mt-2">Get Started Free</router-link>
          </div>
        </div>
      </div>
    </header>

    <!-- Hero Section -->
    <section class="relative min-h-screen flex items-center justify-center overflow-hidden">
      <!-- Animated Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
      <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent"></div>
      
      <!-- Animated Grid -->
      <div class="absolute inset-0 bg-grid-pattern opacity-10"></div>
      
      <!-- Floating Particles -->
      <div class="absolute inset-0">
        <div class="particle particle-1"></div>
        <div class="particle particle-2"></div>
        <div class="particle particle-3"></div>
        <div class="particle particle-4"></div>
        <div class="particle particle-5"></div>
      </div>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <!-- Content -->
          <div class="text-center lg:text-left">
            <!-- Badge -->
            <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-8">
              <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
              AI-Powered Oilfield Platform
            </div>

            <h1 class="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              The Future of
              <span class="block bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent animate-gradient">
                Oilfield Operations
            </span>
          </h1>
            
            <p class="text-xl md:text-2xl text-slate-300 mb-8 max-w-2xl leading-relaxed">
              Harness the power of AI to streamline scheduling, accelerate payments, 
              and ensure compliance. Built for the next generation of oilfield professionals.
            </p>
            
            <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center mb-12">
              <router-link to="/register" class="btn-hero-primary group">
                Start Free Trial
                <div class="absolute inset-0 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                <svg class="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                </svg>
            </router-link>
              
              <button @click="playDemo" class="btn-hero-secondary group">
                <div class="flex items-center justify-center w-8 h-8 bg-white/10 rounded-full mr-3 group-hover:bg-emerald-400/20 transition-colors">
                  <svg class="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"/>
                  </svg>
                </div>
              Watch Demo
              </button>
          </div>

            <!-- Hero Stats -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl mx-auto lg:mx-0">
              <div class="text-center lg:text-left">
                <div class="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-1">1,200+</div>
                <div class="text-slate-400 text-sm">Active Users</div>
              </div>
              <div class="text-center lg:text-left">
                <div class="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-1">$2.5M+</div>
                <div class="text-slate-400 text-sm">Processed</div>
              </div>
              <div class="text-center lg:text-left">
                <div class="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-1">500+</div>
                <div class="text-slate-400 text-sm">Companies</div>
              </div>
              <div class="text-center lg:text-left">
                <div class="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-1">99.9%</div>
                <div class="text-slate-400 text-sm">Uptime</div>
              </div>
            </div>
          </div>

          <!-- Hero Image/Video Container -->
          <div class="relative">
            <div class="hero-image-container">
              <!-- Placeholder for hero image/video -->
              <div class="hero-media-placeholder">
                <div v-if="!showVideo" class="hero-image">
                  <!-- Dashboard Preview Image Placeholder -->
                  <div class="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl border border-slate-700/50 p-6 shadow-2xl shadow-emerald-500/10 cursor-pointer" @click="playDemo">
                    <!-- Mock Dashboard -->
                    <div class="bg-slate-800/50 rounded-2xl p-4 h-full">
                      <!-- Header -->
                      <div class="flex items-center justify-between mb-6">
                        <div class="flex items-center space-x-3">
                          <div class="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-lg"></div>
                          <div class="text-white font-semibold">OilBuddy Dashboard</div>
                        </div>
                        <div class="flex space-x-2">
                          <div class="w-3 h-3 bg-red-500 rounded-full"></div>
                          <div class="w-3 h-3 bg-yellow-500 rounded-full"></div>
                          <div class="w-3 h-3 bg-green-500 rounded-full"></div>
                        </div>
                      </div>
                      
                      <!-- Stats Grid -->
                      <div class="grid grid-cols-2 gap-4 mb-6">
                        <div class="bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 rounded-xl p-4">
                          <div class="text-emerald-400 text-sm mb-1">Active Jobs</div>
                          <div class="text-white text-2xl font-bold">24</div>
                        </div>
                        <div class="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-4">
                          <div class="text-blue-400 text-sm mb-1">Earnings</div>
                          <div class="text-white text-2xl font-bold">$12.5K</div>
                        </div>
                      </div>
                      
                      <!-- Chart Area -->
                      <div class="bg-slate-900/50 rounded-xl p-4 h-32 border border-slate-600/30">
                        <div class="flex items-end justify-between h-full">
                          <div class="w-4 bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t" style="height: 60%"></div>
                          <div class="w-4 bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t" style="height: 80%"></div>
                          <div class="w-4 bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t" style="height: 45%"></div>
                          <div class="w-4 bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t" style="height: 90%"></div>
                          <div class="w-4 bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t" style="height: 70%"></div>
                          <div class="w-4 bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t" style="height: 55%"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Play Button Overlay -->
                  <div class="absolute inset-0 flex items-center justify-center">
                    <button @click="playDemo" class="play-button group">
                      <svg class="w-8 h-8 text-white group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"/>
                      </svg>
                    </button>
                  </div>
                </div>

                                <!-- Video Player -->
                <div v-else class="hero-video">
                  <div class="w-full h-full rounded-3xl overflow-hidden relative">
                    <div class="" style="width: 100%; height: 100%; position: relative; padding-top: 56.25%">
                      <iframe 
                        style="position: absolute; top: 0; left: 0; width: 100%; height: 100%" 
                        allowtransparency="true"   
                        src="https://adilo.bigcommand.com/watch/3CAUjCKB?autoplay=0"  
                        frameborder="0" 
                        allowfullscreen 
                        mozallowfullscreen 
                        webkitallowfullscreen 
                        oallowfullscreen 
                        msallowfullscreen 
                        scrolling="no"
                        allow="autoplay; fullscreen"
                      ></iframe>
                    </div>
                    
                    <!-- Video Controls Overlay - Only show initially -->
                    <div ref="videoOverlay" class="absolute inset-0 flex items-center justify-center bg-black/20 rounded-3xl">
                      <button @click="playVideo" class="play-button group">
                        <svg class="w-8 h-8 text-white group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"/>
                        </svg>
                      </button>
                    </div>
                    
                    <!-- Return to Image Button -->
                    <div class="absolute top-4 right-4 z-10">
                      <button @click="toggleMedia" class="bg-black/50 backdrop-blur-sm border border-white/20 text-white px-3 py-2 rounded-lg hover:bg-black/70 transition-all duration-200">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Floating Elements -->
              <div class="floating-element floating-element-1">
                <div class="w-16 h-16 bg-gradient-to-br from-emerald-400/20 to-cyan-400/20 border border-emerald-400/30 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                  <svg class="w-8 h-8 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
              </div>

              <div class="floating-element floating-element-2">
                <div class="w-20 h-20 bg-gradient-to-br from-blue-400/20 to-purple-400/20 border border-blue-400/30 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                  <svg class="w-10 h-10 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9z"/>
                  </svg>
                </div>
              </div>

              <div class="floating-element floating-element-3">
                <div class="w-14 h-14 bg-gradient-to-br from-purple-400/20 to-pink-400/20 border border-purple-400/30 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                  <svg class="w-7 h-7 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Scroll Indicator -->
      <div class="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <div class="scroll-indicator">
          <div class="scroll-dot"></div>
        </div>
      </div>
    </section>

    <!-- Trust Indicators -->
    <section class="py-16 bg-slate-800/50 border-b border-slate-700/50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <p class="text-slate-400 mb-12 text-lg">Trusted by oilfield professionals across North Dakota</p>
          <div class="grid grid-cols-2 md:grid-cols-5 gap-8 items-center">
            <div class="company-logo">
              <div class="text-slate-300 font-bold text-xl">Bakken Energy</div>
          </div>
            <div class="company-logo">
              <div class="text-slate-300 font-bold text-xl">Williston Oilfield</div>
            </div>
            <div class="company-logo">
              <div class="text-slate-300 font-bold text-xl">Dickinson Drilling</div>
            </div>
            <div class="company-logo">
              <div class="text-slate-300 font-bold text-xl">Minot Production</div>
            </div>
            <div class="company-logo">
              <div class="text-slate-300 font-bold text-xl">Fargo Field</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Key Benefits -->
    <section id="features" class="py-24 bg-slate-900">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-20">
          <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
            AI-Powered Features
          </div>
          <h2 class="text-4xl md:text-5xl font-bold text-white mb-6">
            Everything You Need to Run Your
            <span class="block bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Oilfield Business
            </span>
          </h2>
          <p class="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            From AI-powered job matching to automated compliance, OilBuddy handles the complex parts 
            so you can focus on what you do best.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="benefit-card-modern group">
            <div class="benefit-icon-modern bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border-emerald-500/30">
              <svg class="w-8 h-8 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h3 class="benefit-title-modern">AI Job Matching</h3>
            <p class="benefit-description-modern">
              Advanced algorithms analyze your skills, location, and preferences to connect you with the highest-paying opportunities.
            </p>
            <div class="benefit-arrow">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
              </svg>
            </div>
          </div>

          <div class="benefit-card-modern group">
            <div class="benefit-icon-modern bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-blue-500/30">
              <svg class="w-8 h-8 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9z"/>
              </svg>
            </div>
            <h3 class="benefit-title-modern">Instant Payments</h3>
            <p class="benefit-description-modern">
              Digital timesheets and smart contracts ensure you get paid within hours, not weeks. Real-time payment tracking included.
            </p>
            <div class="benefit-arrow">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
              </svg>
            </div>
          </div>

          <div class="benefit-card-modern group">
            <div class="benefit-icon-modern bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30">
              <svg class="w-8 h-8 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
            </div>
            <h3 class="benefit-title-modern">Smart Compliance</h3>
            <p class="benefit-description-modern">
              AI monitors your certifications and automatically alerts you before expiration. Never miss a deadline again.
            </p>
            <div class="benefit-arrow">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works -->
    <section id="how-it-works" class="py-24 bg-slate-900">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-20">
          <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
            How OilBuddy Works
          </div>
          <h2 class="text-4xl md:text-5xl font-bold text-white mb-6">
            Get Started in
            <span class="block bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              4 Simple Steps
            </span>
          </h2>
          <p class="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Transform how you manage your oilfield operations with our AI-powered platform
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div class="step-card-modern group">
            <div class="step-number-modern">
              <span class="step-inner">1</span>
              <div class="step-glow"></div>
            </div>
            <h3 class="step-title-modern">Create AI Profile</h3>
            <p class="step-description-modern">
              Set up your contractor or company profile with skills, certifications, and preferences. AI learns your patterns.
            </p>
            <div class="step-connector"></div>
          </div>

          <div class="step-card-modern group">
            <div class="step-number-modern">
              <span class="step-inner">2</span>
              <div class="step-glow"></div>
            </div>
            <h3 class="step-title-modern">Smart Job Matching</h3>
            <p class="step-description-modern">
              Browse exclusive oilfield jobs or get AI-powered matches based on your profile and earning potential.
            </p>
            <div class="step-connector"></div>
          </div>

          <div class="step-card-modern group">
            <div class="step-number-modern">
              <span class="step-inner">3</span>
              <div class="step-glow"></div>
            </div>
            <h3 class="step-title-modern">Work & Track</h3>
            <p class="step-description-modern">
              Complete jobs with digital timesheets, automated expense tracking, and real-time safety reporting.
            </p>
            <div class="step-connector"></div>
          </div>

          <div class="step-card-modern group">
            <div class="step-number-modern">
              <span class="step-inner">4</span>
              <div class="step-glow"></div>
            </div>
            <h3 class="step-title-modern">Get Paid Instantly</h3>
            <p class="step-description-modern">
              Receive payments within hours with smart contracts, automated invoicing, and AI tax optimization.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Grid -->
    <section class="py-24 bg-slate-800/50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-20">
          <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
            Advanced Features
          </div>
          <h2 class="text-4xl md:text-5xl font-bold text-white mb-6">
            Powerful Features for Modern
            <span class="block bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Oilfield Operations
            </span>
          </h2>
          <p class="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Built specifically for the unique challenges of oilfield contracting with cutting-edge AI technology
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div class="feature-card-modern group">
            <div class="feature-icon-modern bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border-emerald-500/30">
              <svg class="w-7 h-7 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h3 class="feature-title-modern">AI-Powered Job Matching</h3>
            <p class="feature-description-modern">Smart algorithms analyze your skills, location, and earnings history to match you with the highest-paying opportunities</p>
          </div>

          <div class="feature-card-modern group">
            <div class="feature-icon-modern bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-blue-500/30">
              <svg class="w-7 h-7 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/>
              </svg>
            </div>
            <h3 class="feature-title-modern">Digital Field Tickets</h3>
            <p class="feature-description-modern">Replace paper tickets with blockchain-verified digital approvals and instant data synchronization</p>
          </div>

          <div class="feature-card-modern group">
            <div class="feature-icon-modern bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30">
              <svg class="w-7 h-7 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9z"/>
              </svg>
            </div>
            <h3 class="feature-title-modern">Smart Expense Tracking</h3>
            <p class="feature-description-modern">AI automatically tracks mileage, per diem, and equipment expenses with GPS and receipt scanning</p>
          </div>

          <div class="feature-card-modern group">
            <div class="feature-icon-modern bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border-cyan-500/30">
              <svg class="w-7 h-7 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
              </svg>
            </div>
            <h3 class="feature-title-modern">Predictive Scheduling</h3>
            <p class="feature-description-modern">AI learns North Dakota rotation patterns and predicts optimal scheduling with calendar sync</p>
          </div>

          <div class="feature-card-modern group">
            <div class="feature-icon-modern bg-gradient-to-br from-orange-500/20 to-red-500/20 border-orange-500/30">
              <svg class="w-7 h-7 text-orange-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
            </div>
            <h3 class="feature-title-modern">AI Safety Compliance</h3>
            <p class="feature-description-modern">Automated certification tracking, safety training reminders, and predictive incident reporting</p>
          </div>

          <div class="feature-card-modern group">
            <div class="feature-icon-modern bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
              <svg class="w-7 h-7 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 000 2v9a2 2 0 002 2h1a1 1 0 100-2H5V6a1 1 0 00-1-1H3z"/>
                <path d="M8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"/>
              </svg>
            </div>
            <h3 class="feature-title-modern">AI Tax Optimization</h3>
            <p class="feature-description-modern">Machine learning optimizes deductions, quarterly estimates, and year-end reporting for maximum savings</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="py-24 bg-slate-800">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-20">
          <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
            Flexible Pricing
          </div>
          <h2 class="text-4xl md:text-5xl font-bold text-white mb-6">
            Simple, Transparent
            <span class="block bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Pricing
            </span>
        </h2>
          <p class="text-xl text-slate-300 max-w-3xl mx-auto mb-12 leading-relaxed">
            Choose the plan that fits your needs. All plans include a 14-day free trial with full access.
          </p>
          
          <!-- Billing Toggle -->
          <div class="flex items-center justify-center mb-16">
            <span class="text-slate-300 mr-4 font-medium">Monthly</span>
            <button 
              @click="billingAnnual = !billingAnnual"
              :class="['relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 shadow-lg', billingAnnual ? 'bg-gradient-to-r from-emerald-500 to-cyan-500' : 'bg-slate-600']"
            >
              <span :class="['inline-block h-5 w-5 transform rounded-full bg-white transition-all duration-300 shadow-md', billingAnnual ? 'translate-x-6' : 'translate-x-1']"></span>
            </button>
            <span class="text-slate-300 ml-4 font-medium">
              Yearly
              <span class="inline-flex items-center px-2 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400 text-xs font-medium ml-2">
                Save 20%
              </span>
            </span>
          </div>
        </div>

        <div v-if="plansLoading" class="text-center">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400"></div>
          <p class="text-slate-300 mt-2">Loading plans...</p>
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div 
            v-for="plan in activePlans" 
            :key="plan.id" 
            :class="['pricing-card', plan.popular ? 'pricing-card-popular' : '']"
          >
            <!-- Popular Badge -->
            <div v-if="plan.popular" class="popular-badge">
              Most Popular
            </div>

            <div class="pricing-header">
              <h3 class="plan-name">{{ plan.name }}</h3>
              <p class="plan-desc">{{ plan.description }}</p>
              
              <div class="plan-pricing">
                <span class="plan-price">
                  ${{ billingAnnual && plan.billingPeriod === 'month' ? Math.round(plan.price * 12 * 0.8) : plan.price }}
                </span>
                <span class="plan-period">
                  /{{ billingAnnual && plan.billingPeriod === 'month' ? 'year' : plan.billingPeriod }}
                </span>
              </div>
              
              <div v-if="billingAnnual && plan.billingPeriod === 'month'" class="savings-badge">
                Save ${{ Math.round(plan.price * 12 * 0.2) }} per year
              </div>
            </div>

            <div class="pricing-body">
              <ul class="plan-features">
                <li v-for="feature in plan.features" :key="feature" class="plan-feature">
                  <svg class="w-5 h-5 text-success-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                  {{ feature }}
                </li>
              </ul>

              <div v-if="plan.limits" class="plan-limits">
                <div v-if="plan.limits.jobs" class="limit-row">
                  <span class="limit-label">Jobs per month:</span>
                  <span class="limit-value">{{ plan.limits.jobs === -1 ? 'Unlimited' : plan.limits.jobs }}</span>
                </div>
                <div v-if="plan.limits.users" class="limit-row">
                  <span class="limit-label">Team members:</span>
                  <span class="limit-value">{{ plan.limits.users === -1 ? 'Unlimited' : plan.limits.users }}</span>
                </div>
                <div v-if="plan.limits.storage" class="limit-row">
                  <span class="limit-label">Storage:</span>
                  <span class="limit-value">{{ plan.limits.storage === -1 ? 'Unlimited' : plan.limits.storage + 'GB' }}</span>
                </div>
              </div>
            </div>

            <div class="pricing-footer">
              <router-link 
                :to="`/register?plan=${plan.id}`" 
                :class="['btn-cta', plan.popular ? 'btn-cta-popular' : 'btn-cta-default']"
              >
            Start Free Trial
          </router-link>
              <p class="trial-text">14-day free trial • No credit card required</p>
        </div>
          </div>
        </div>


      </div>
    </section>

    <!-- Testimonials -->
    <section id="testimonials" class="py-24 bg-slate-900">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-20">
          <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
            User Reviews
          </div>
          <h2 class="text-4xl md:text-5xl font-bold text-white mb-6">
            What Our Users
            <span class="block bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Say About Us
            </span>
          </h2>
          <p class="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            See how OilBuddy's AI is transforming oilfield operations across North Dakota
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="testimonial-card-modern group">
            <div class="testimonial-content-modern">
              <div class="testimonial-stars">
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
              </div>
              <p class="testimonial-text-modern">
                "OilBuddy's AI completely revolutionized my contracting business. I'm getting paid 3x faster and the smart matching found me jobs I never would have discovered. Game changer!"
              </p>
            </div>
            <div class="testimonial-author-modern">
              <div class="author-avatar-modern">
                <span>JD</span>
              </div>
          <div>
                <div class="author-name-modern">Jake Daniels</div>
                <div class="author-role-modern">Flowback Specialist</div>
                <div class="author-location">Williston, ND</div>
              </div>
            </div>
          </div>

          <div class="testimonial-card-modern group">
            <div class="testimonial-content-modern">
              <div class="testimonial-stars">
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
              </div>
              <p class="testimonial-text-modern">
                "The predictive scheduling is incredible. AI learns our rotation patterns and prevents conflicts. My crew loves the mobile app and real-time updates."
              </p>
            </div>
            <div class="testimonial-author-modern">
              <div class="author-avatar-modern">
                <span>SM</span>
              </div>
              <div>
                <div class="author-name-modern">Sarah Mitchell</div>
                <div class="author-role-modern">Operations Manager</div>
                <div class="author-location">Dickinson, ND</div>
              </div>
            </div>
          </div>

          <div class="testimonial-card-modern group">
            <div class="testimonial-content-modern">
              <div class="testimonial-stars">
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
                <svg class="w-5 h-5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                </svg>
              </div>
              <p class="testimonial-text-modern">
                "Tax season used to be a nightmare. The AI tax optimization saved me thousands and everything is organized automatically. Best investment I've made."
            </p>
          </div>
            <div class="testimonial-author-modern">
              <div class="author-avatar-modern">
                <span>MR</span>
              </div>
              <div>
                <div class="author-name-modern">Mike Rodriguez</div>
                <div class="author-role-modern">Independent Contractor</div>
                <div class="author-location">Minot, ND</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Custom Solutions Section -->
    <section class="py-24 bg-slate-800/50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
          <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
          Enterprise Solutions
        </div>
        <h2 class="text-4xl md:text-5xl font-bold text-white mb-6">
          Need Something
          <span class="block bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
            Custom?
          </span>
        </h2>
        <p class="text-xl text-slate-300 mb-12 max-w-3xl mx-auto leading-relaxed">
          Our AI platform can be customized for large enterprises with specific workflows, integrations, and compliance requirements.
        </p>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <div class="custom-feature">
            <div class="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z" clip-rule="evenodd"/>
              </svg>
            </div>
            <h3 class="text-lg font-bold text-white mb-2">Custom Integrations</h3>
            <p class="text-slate-400 text-sm">Connect with your existing ERP, accounting, and field management systems</p>
          </div>
          <div class="custom-feature">
            <div class="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd"/>
              </svg>
            </div>
            <h3 class="text-lg font-bold text-white mb-2">Dedicated Support</h3>
            <p class="text-slate-400 text-sm">24/7 priority support with dedicated account management</p>
          </div>
          <div class="custom-feature">
            <div class="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
              </svg>
            </div>
            <h3 class="text-lg font-bold text-white mb-2">Enhanced Security</h3>
            <p class="text-slate-400 text-sm">Enterprise-grade security with SOC 2 compliance and custom data policies</p>
          </div>
        </div>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <router-link to="/contact" class="btn-hero-primary">
            Contact Enterprise Sales
            <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
            </svg>
          </router-link>
          <a href="tel:+1-701-555-0123" class="btn-hero-secondary">
            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
            </svg>
            Call (701) 555-0123
          </a>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-24 bg-gradient-to-br from-slate-900 via-emerald-900/20 to-slate-900">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div class="relative">
          <!-- Background glow -->
          <div class="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-3xl blur-3xl"></div>
          
          <div class="relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-12">
            <h2 class="text-4xl md:text-6xl font-bold text-white mb-6">
              Ready to Transform Your
              <span class="block bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                Oilfield Operations?
              </span>
            </h2>
            <p class="text-xl text-slate-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              Join thousands of contractors and companies who are already using OilBuddy's AI to streamline operations and increase profits.
            </p>
            <div class="flex flex-col sm:flex-row gap-6 justify-center mb-8">
              <router-link to="/register" class="btn-hero-primary group">
                Start Your Free Trial
                <div class="absolute inset-0 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                <svg class="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
                </svg>
              </router-link>
              <router-link to="/contact" class="btn-hero-secondary">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                </svg>
                Contact Sales
              </router-link>
            </div>
            <div class="flex flex-wrap justify-center gap-8 text-slate-400 text-sm">
              <div class="flex items-center">
                <svg class="w-4 h-4 text-emerald-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
                14-day free trial
              </div>
              <div class="flex items-center">
                <svg class="w-4 h-4 text-emerald-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
                No credit card required
              </div>
              <div class="flex items-center">
                <svg class="w-4 h-4 text-emerald-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
                Cancel anytime
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-slate-950 text-white py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-12">
          <!-- Company Info -->
          <div class="col-span-1 md:col-span-2">
            <div class="flex items-center space-x-3 mb-6">
              <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy Logo" class="w-12 h-12 rounded-2xl shadow-lg shadow-emerald-500/25" />
              <div>
                <span class="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">OilBuddy</span>
                <span class="block text-sm text-slate-400 font-medium">AI-Powered Oilfield Platform</span>
              </div>
            </div>
            <p class="text-slate-300 mb-8 max-w-md leading-relaxed">
              The complete AI-powered platform for oilfield contractors and companies. Streamline operations, get paid faster, and stay compliant with cutting-edge technology.
            </p>
            <div class="flex space-x-6">
              <a href="#" class="footer-social-link group">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z" clip-rule="evenodd"/>
                </svg>
              </a>
              <a href="#" class="footer-social-link group">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84"/>
                </svg>
              </a>
              <a href="#" class="footer-social-link group">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clip-rule="evenodd"/>
                </svg>
              </a>
              <a href="#" class="footer-social-link group">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M12.017 0H7.984C3.467 0 0 3.467 0 7.984v4.033C0 16.533 3.467 20 7.984 20h4.033C16.533 20 20 16.533 20 12.017V7.984C20 3.467 16.533 0 12.017 0zm3.734 7.984c0 .367-.298.666-.666.666s-.667-.299-.667-.666.299-.666.667-.666.666.299.666.666zM10 5c2.757 0 5 2.243 5 5s-2.243 5-5 5-5-2.243-5-5 2.243-5 5-5zm0 8c1.654 0 3-1.346 3-3s-1.346-3-3-3-3 1.346-3 3 1.346 3 3 3z" clip-rule="evenodd"/>
                </svg>
              </a>
            </div>
          </div>

          <!-- Product Links -->
          <div>
            <h3 class="text-lg font-bold text-white mb-6">Product</h3>
            <ul class="space-y-4">
              <li><a href="#features" class="footer-link">AI Features</a></li>
              <li><a href="#pricing" class="footer-link">Pricing Plans</a></li>
              <li><a href="#how-it-works" class="footer-link">How It Works</a></li>
              <li><router-link to="/blog" class="footer-link">Blog</router-link></li>
              <li><router-link to="/register" class="footer-link">Start Free Trial</router-link></li>
              <li><router-link to="/contact" class="footer-link">Enterprise</router-link></li>
            </ul>
          </div>

          <!-- Support & Company -->
          <div>
            <h3 class="text-lg font-bold text-white mb-6">Company</h3>
            <ul class="space-y-4">
              <li><router-link to="/support" class="footer-link">Support Center</router-link></li>
              <li><router-link to="/contact" class="footer-link">Contact Us</router-link></li>
              <li><router-link to="/roadmap" class="footer-link">Roadmap</router-link></li>
              <li><router-link to="/tokens" class="footer-link">Tokens</router-link></li>
              <li><a href="/#/login" class="footer-link">Sign In</a></li>
              <li><a href="/#/admin/login" class="footer-link">Admin</a></li>
              <li><router-link to="/privacy" class="footer-link">Privacy Policy</router-link></li>
              <li><router-link to="/terms" class="footer-link">Terms of Service</router-link></li>
              <li><router-link to="/cookies" class="footer-link">Cookie Policy</router-link></li>
              <li><router-link to="/data-deletion" class="footer-link">Data Deletion</router-link></li>
            </ul>
          </div>
        </div>

        <!-- Bottom Bar -->
        <div class="border-t border-slate-800 mt-16 pt-8">
          <div class="flex flex-col md:flex-row justify-between items-center">
            <div class="flex items-center space-x-6 mb-4 md:mb-0">
              <p class="text-slate-400 text-sm">
                © {{ new Date().getFullYear() }} OilBuddy AI. All rights reserved.
              </p>
              <span class="text-slate-600">•</span>
              <p class="text-slate-500 text-sm">
                Built for the North Dakota oilfield
              </p>
            </div>
            <div class="flex items-center space-x-4">
              <span class="inline-flex items-center px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-xs font-medium">
                <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
                AI-Powered
              </span>
              <span class="inline-flex items-center px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-xs font-medium">
                SOC 2 Compliant
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { plansService, type Plan } from '@/services/plansService'
import { onMounted, ref } from 'vue'

// State
const mobileMenuOpen = ref(false)
const billingAnnual = ref(false)
const activePlans = ref<Plan[]>([])
const plansLoading = ref(true)
const showVideo = ref(false)
const isVideoPlaying = ref(false)
const videoIframe = ref<HTMLIFrameElement | null>(null)
const videoOverlay = ref<HTMLElement | null>(null)

// Methods
const loadPlans = async () => {
  try {
    plansLoading.value = true
    const plans = await plansService.getActivePlans()
    activePlans.value = plans
  } catch (error) {
    console.error('Error loading plans:', error)
  } finally {
    plansLoading.value = false
  }
}

const playDemo = () => {
  showVideo.value = true
  // Hide the overlay immediately when play button is clicked
  setTimeout(() => {
    if (videoOverlay.value) {
      videoOverlay.value.style.display = 'none'
    }
  }, 50)
}

const toggleMedia = () => {
  showVideo.value = !showVideo.value
  if (!showVideo.value) {
    // Reset video when returning to image
    isVideoPlaying.value = false
  } else {
    // When switching to video, hide the overlay
    setTimeout(() => {
      if (videoOverlay.value) {
        videoOverlay.value.style.display = 'none'
      }
    }, 100)
  }
}

const playVideo = () => {
  // For iframe, we need to change the source to trigger autoplay
  const iframe = document.querySelector('.hero-video iframe') as HTMLIFrameElement
  if (iframe) {
    iframe.src = 'https://adilo.bigcommand.com/watch/3CAUjCKB?autoplay=1'
  }
  isVideoPlaying.value = true
  // Hide the overlay immediately when play button is clicked
  if (videoOverlay.value) {
    videoOverlay.value.style.display = 'none'
  }
}

const toggleVideo = () => {
  // For iframe, clicking will handle play/pause through the iframe's own controls
  isVideoPlaying.value = !isVideoPlaying.value
}

const onVideoEnded = () => {
  // For iframe, we can't detect when video ends, so we'll keep this for compatibility
  isVideoPlaying.value = false
}

// Lifecycle
onMounted(() => {
  loadPlans()
})
</script>

<style scoped>
/* Modern AI-style buttons */
.btn-primary-modern {
  @apply inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-2xl hover:from-emerald-400 hover:to-cyan-400 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}

.btn-hero-primary {
  @apply relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold rounded-2xl text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl shadow-emerald-500/25 overflow-hidden;
}

.btn-hero-secondary {
  @apply inline-flex items-center px-6 py-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-2xl hover:bg-white/20 transition-all duration-300 transform hover:scale-105;
}

.btn-outline {
  @apply inline-flex items-center px-6 py-3 border-2 border-slate-600 text-slate-300 font-semibold rounded-xl hover:bg-slate-600 hover:text-white transition-all duration-200;
}

.btn-white {
  @apply inline-flex items-center px-6 py-3 bg-white text-slate-900 font-semibold rounded-xl hover:bg-slate-100 transition-all duration-200 transform hover:scale-105;
}

.btn-outline-white {
  @apply inline-flex items-center px-6 py-3 border-2 border-white text-white font-semibold rounded-xl hover:bg-white hover:text-slate-900 transition-all duration-200;
}

.btn-lg {
  @apply px-8 py-4 text-lg;
}

/* Background patterns and animations */
.bg-grid-pattern {
  background-image: radial-gradient(circle, rgba(148, 163, 184, 0.3) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: grid-move 20s linear infinite;
}

@keyframes grid-move {
  0% { background-position: 0 0; }
  100% { background-position: 50px 50px; }
}

/* Animated gradient text */
.animate-gradient {
  background-size: 300% 300%;
  animation: gradient-shift 3s ease infinite;
}

@keyframes gradient-shift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

/* Floating particles */
.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: linear-gradient(45deg, #10b981, #06b6d4);
  border-radius: 50%;
  opacity: 0.6;
  animation: float 6s ease-in-out infinite;
}

.particle-1 { top: 20%; left: 10%; animation-delay: 0s; }
.particle-2 { top: 60%; left: 20%; animation-delay: 1s; }
.particle-3 { top: 40%; left: 80%; animation-delay: 2s; }
.particle-4 { top: 80%; left: 70%; animation-delay: 3s; }
.particle-5 { top: 30%; left: 60%; animation-delay: 4s; }

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.6; }
  50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
}

/* Hero image/video container */
.hero-image-container {
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.hero-media-placeholder {
  position: relative;
  width: 100%;
  height: 400px;
  cursor: pointer;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

.hero-image,
.hero-video {
  width: 100%;
  height: 100%;
  border-radius: 24px;
  overflow: hidden;
}

.play-button {
  @apply w-20 h-20 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-300 shadow-2xl;
}

/* Floating elements */
.floating-element {
  position: absolute;
  animation: float-element 4s ease-in-out infinite;
}

.floating-element-1 {
  top: 10%;
  right: -8%;
  animation-delay: 0s;
}

.floating-element-2 {
  bottom: 20%;
  right: -10%;
  animation-delay: 1.5s;
}

.floating-element-3 {
  top: 60%;
  left: -6%;
  animation-delay: 3s;
}

@keyframes float-element {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-15px) rotate(5deg); }
}

/* Scroll indicator */
.scroll-indicator {
  @apply w-6 h-10 border-2 border-white/30 rounded-full flex justify-center;
}

.scroll-dot {
  @apply w-1 h-3 bg-white/60 rounded-full mt-2;
  animation: scroll-bounce 2s infinite;
}

@keyframes scroll-bounce {
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(8px); }
  60% { transform: translateY(4px); }
}

/* Modern benefit cards */
.benefit-card-modern {
  @apply relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-8 hover:border-emerald-500/30 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-emerald-500/10;
}

.benefit-icon-modern {
  @apply w-16 h-16 rounded-2xl border flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110;
}

.benefit-title-modern {
  @apply text-xl font-bold text-white mb-4 group-hover:text-emerald-400 transition-colors duration-300;
}

.benefit-description-modern {
  @apply text-slate-400 leading-relaxed mb-6;
}

.benefit-arrow {
  @apply text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-2 transition-all duration-300;
}

/* Company logos */
.company-logo {
  @apply opacity-60 hover:opacity-100 transition-opacity duration-300 transform hover:scale-105;
}

/* Pricing cards - update existing styles */
.pricing-card {
  @apply relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-8 hover:border-emerald-500/30 transition-all duration-500 hover:shadow-2xl hover:shadow-emerald-500/10;
}

.pricing-card-popular {
  @apply border-emerald-500/50 bg-gradient-to-br from-slate-800/80 to-emerald-900/20 shadow-2xl shadow-emerald-500/20 transform scale-105;
}

.popular-badge {
  @apply absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg;
}

.pricing-header {
  @apply text-center mb-8;
}

.plan-name {
  @apply text-2xl font-bold text-white mb-2;
}

.plan-desc {
  @apply text-slate-300 mb-6;
}

.plan-pricing {
  @apply mb-8;
}

.plan-price {
  @apply text-4xl font-bold text-white;
}

.plan-period {
  @apply text-slate-400 text-lg;
}

.plan-features {
  @apply space-y-4 mb-8;
}

.feature-item {
  @apply flex items-center text-white;
}

.feature-check {
  @apply w-5 h-5 text-emerald-400 mr-3 flex-shrink-0;
}

.plan-cta {
  @apply w-full py-4 px-6 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-bold rounded-2xl hover:from-emerald-400 hover:to-cyan-400 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}

.plan-cta-outline {
  @apply w-full py-4 px-6 border-2 border-slate-600 text-slate-300 font-bold rounded-2xl hover:bg-slate-600 hover:text-white transition-all duration-300;
}

/* Step cards for How It Works */
.step-card-modern {
  @apply relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-8 text-center hover:border-emerald-500/30 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-emerald-500/10;
}

.step-number-modern {
  @apply relative w-20 h-20 mx-auto mb-6 flex items-center justify-center;
}

.step-inner {
  @apply relative z-10 text-2xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent;
}

.step-glow {
  @apply absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-full border border-emerald-500/30 group-hover:from-emerald-400/30 group-hover:to-cyan-400/30 transition-all duration-300;
}

.step-title-modern {
  @apply text-xl font-bold text-white mb-4 group-hover:text-emerald-400 transition-colors duration-300;
}

.step-description-modern {
  @apply text-slate-400 leading-relaxed;
}

.step-connector {
  @apply hidden md:block absolute top-10 -right-4 w-8 h-0.5 bg-gradient-to-r from-emerald-500/50 to-transparent;
}

.step-card-modern:last-child .step-connector {
  @apply hidden;
}

/* Feature cards modern */
.feature-card-modern {
  @apply relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-8 hover:border-emerald-500/30 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-emerald-500/10;
}

.feature-icon-modern {
  @apply w-16 h-16 rounded-2xl border flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110;
}

.feature-title-modern {
  @apply text-xl font-bold text-white mb-4 group-hover:text-emerald-400 transition-colors duration-300;
}

.feature-description-modern {
  @apply text-slate-400 leading-relaxed;
}

/* Testimonial cards modern */
.testimonial-card-modern {
  @apply relative bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-8 hover:border-emerald-500/30 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-emerald-500/10;
}

.testimonial-content-modern {
  @apply mb-6;
}

.testimonial-stars {
  @apply flex space-x-1 mb-4;
}

.testimonial-text-modern {
  @apply text-slate-300 text-lg leading-relaxed italic;
}

.testimonial-author-modern {
  @apply flex items-center;
}

.author-avatar-modern {
  @apply w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center font-bold text-slate-900 mr-4;
}

.author-name-modern {
  @apply font-semibold text-white;
}

.author-role-modern {
  @apply text-slate-400 text-sm;
}

.author-location {
  @apply text-emerald-400 text-xs font-medium;
}

/* Footer styles */
.footer-social-link {
  @apply w-12 h-12 bg-slate-800/50 border border-slate-700/50 rounded-xl flex items-center justify-center text-slate-400 hover:text-emerald-400 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all duration-300;
}

.footer-link {
  @apply text-slate-400 hover:text-emerald-400 transition-colors duration-300 font-medium;
}

/* Update existing styles for dark theme */
.benefit-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-3xl p-8 hover:border-emerald-500/30 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-emerald-500/10;
}

.benefit-icon {
  @apply w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto;
}

.benefit-title {
  @apply text-xl font-bold text-oil-900 mb-4 text-center;
}

.benefit-description {
  @apply text-oil-600 text-center leading-relaxed;
}

.step-card {
  @apply text-center;
}

.step-number {
  @apply w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-primary-600;
}

.step-title {
  @apply text-lg font-semibold text-oil-900 mb-3;
}

.step-description {
  @apply text-oil-700;
}

.feature-card {
  @apply bg-white p-6 rounded-lg border border-oil-100 hover:shadow-md transition-shadow;
}

.feature-icon {
  @apply w-12 h-12 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center mb-4;
}

.feature-title {
  @apply font-semibold text-oil-900 mb-2;
}

.feature-description {
  @apply text-sm text-oil-700;
}



.pricing-header {
  @apply text-center mb-8;
}

.plan-name {
  @apply text-2xl font-bold text-white mb-2;
}

.plan-desc {
  @apply text-slate-300 mb-6;
}

.plan-pricing {
  @apply flex items-baseline justify-center mb-4;
}

.plan-price {
  @apply text-5xl font-bold text-white;
}

.plan-period {
  @apply text-slate-400 ml-2;
}

.savings-badge {
  @apply inline-block bg-success-100 text-success-800 px-3 py-1 rounded-full text-sm font-medium;
}

.pricing-body {
  @apply mb-8;
}

.plan-features {
  @apply space-y-4 mb-6;
}

.plan-feature {
  @apply flex items-start text-white;
}

.plan-limits {
  @apply border-t border-slate-700/50 pt-6 space-y-2;
}

.limit-row {
  @apply flex justify-between text-sm;
}

.limit-label {
  @apply text-slate-300;
}

.limit-value {
  @apply font-semibold text-white;
}

.pricing-footer {
  @apply text-center;
}

.btn-cta {
  @apply w-full py-4 px-6 rounded-2xl font-semibold text-base transition-all duration-300 mb-3 transform hover:scale-105;
}

.btn-cta-popular {
  @apply bg-gradient-to-r from-emerald-500 to-cyan-500 text-white hover:from-emerald-400 hover:to-cyan-400 shadow-lg shadow-emerald-500/25;
}

.btn-cta-default {
  @apply bg-slate-700/50 border border-slate-600/50 text-white hover:bg-slate-600/50 hover:text-white hover:border-emerald-500/30;
}

.trial-text {
  @apply text-xs text-slate-400 mt-4 leading-relaxed;
}

.testimonial-card {
  @apply bg-white p-8 rounded-2xl border border-oil-100 hover:shadow-lg transition-shadow;
}

.testimonial-content {
  @apply mb-6;
}

.testimonial-text {
  @apply text-oil-700 italic text-lg leading-relaxed;
}

.testimonial-author {
  @apply flex items-center;
}

.author-avatar {
  @apply w-12 h-12 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center font-semibold mr-4;
}

.author-name {
  @apply font-semibold text-oil-900;
}

.author-role {
  @apply text-oil-600 text-sm;
}
</style>
